export const SUPPORT_AI_RUN_EVENT_PAGE_LIMIT = 50;
export const SUPPORT_AI_RUN_MAX_CURSOR = 2_147_483_647;

export function isValidSupportAIRunCursor(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 0 && Number(value) <= SUPPORT_AI_RUN_MAX_CURSOR;
}

export function assertValidSupportAIRunCursor(value: unknown): asserts value is number {
  if (!isValidSupportAIRunCursor(value)) throw new RangeError('Invalid support AI run cursor');
}

type SupportAIRunResource = 'detail' | 'events' | 'stream' | 'cancel' | 'retry';

/**
 * Origin every server-supplied run resource URL (stream, poll, retry, cancel)
 * must be bound to. REST calls go to `API_BASE_URL`, so the SSE stream and the
 * retry POST must target the same host — resolving against `location.origin`
 * silently pointed them at the Vite dev server whenever the page and the API
 * were served from different hosts. Falls back to the page origin only when no
 * API base is known (unit tests without a DOM).
 */
export function getSupportAIApiOrigin(apiBaseUrl: string | null | undefined): string {
  if (typeof apiBaseUrl === 'string' && apiBaseUrl) {
    try {
      return new URL(apiBaseUrl).origin;
    } catch {
      // fall through to the page origin
    }
  }
  return globalThis.location?.origin ?? 'https://chat.localhost';
}

export function getBoundSupportAIRunUrl(
  value: unknown,
  binding: { origin: string; ticketNumber: string; runId: string; resource: SupportAIRunResource }
) {
  if (typeof value !== 'string' || !value || value.startsWith('//')) return null;
  const suffix: Record<SupportAIRunResource, string> = {
    detail: '',
    events: 'events/',
    stream: 'events/stream/',
    cancel: 'cancel/',
    retry: 'retry/'
  };
  const expectedPath = `/api/support/tickets/${encodeURIComponent(binding.ticketNumber)}/ai/runs/${encodeURIComponent(binding.runId)}/${suffix[binding.resource]}`;
  try {
    const url = new URL(value, binding.origin);
    if (url.origin !== binding.origin || url.pathname !== expectedPath || url.search || url.hash) return null;
    return url.href;
  } catch {
    return null;
  }
}
